import { ControlProps, OwnPropsOfEnum } from '@jsonforms/core';
import { MaterialInputControl, MuiSelect, WithOptionLabel } from '@jsonforms/material-renderers';
import { TranslateProps } from '@jsonforms/react';
import merge from 'lodash/merge';
import { useContext, useMemo, useEffect, useState } from 'react';
import { useDispatch, connect } from 'react-redux';
import { factoryManagementActions } from '../../../../../store/reducers/factory-management';
import { EditorContext } from '../../context';
import { MuiAutocomplete } from './component';
import { useParams } from 'react-router-dom';
import { useNodes } from 'reactflow';

interface ControllerTypes {
  loadingCopyConfigurationFromList: boolean;
  copyConfigurationFromList: { label: string; value: string }[];
  // copyConfigurationFromComponent: any;
}

export const MaterialOneOfEnumControl = (
  props: ControlProps & OwnPropsOfEnum & WithOptionLabel & TranslateProps & ControllerTypes
) => {
  const { factoryId } = useParams();
  const { activeNode, activeComponentId } = useContext(EditorContext);
  const currentOpt = activeNode?.data?.options?.find((option: any) => option.component_id === activeComponentId);
  const flowNodes = useNodes();
  const [copyValue, setCopyValue] = useState<string | undefined>(props.data);
  const { config, uischema, errors } = props;
  const appliedUiSchemaOptions = merge({}, config, uischema.options);
  const isValid = errors.length === 0;

  useEffect(() => {
    setCopyValue(props.data);
  }, [props.data]);

  const selectedFactoryId = currentOpt?.data?.select_factory_to_copy_configuration_from;
  const effectiveFactoryId = currentOpt?.operation === 'added' && selectedFactoryId === undefined ? factoryId : selectedFactoryId;
  const isFactorySelected = Boolean(effectiveFactoryId && effectiveFactoryId !== 'select');

  useEffect(() => {
    setCopyValue(undefined);
  }, [effectiveFactoryId]);

  const dispatch = useDispatch();

  const resolvedComponentId = useMemo(() => {
    if (activeComponentId !== null && activeComponentId !== undefined) {
      return activeComponentId;
    }

    const matchedIndex = String(props.path || '').match(/options\.(\d+)\.data\.copy_configuration_from/);
    const optionIndex = matchedIndex ? Number(matchedIndex[1]) : -1;
    const option = optionIndex >= 0 ? activeNode?.data?.options?.[optionIndex] : undefined;
    return option?.component_id;
  }, [activeComponentId, activeNode?.data?.options, props.path]);

  const fallbackOptions = useMemo(() => {
    if (resolvedComponentId === null || resolvedComponentId === undefined) return [];

    return (flowNodes || [])
      .filter((node: any) => node?.type === 'assetNode' && node?.id !== activeNode?.id)
      .filter((node: any) => {
        const options = node?.data?.options || [];
        return options.some((option: any) => String(option?.component_id) === String(resolvedComponentId));
      })
      .map((node: any) => ({
        label: `${node?.data?.name || node?.id} (${node?.data?.ip || 'NA'})`,
        value: node?.id,
      }));
  }, [activeNode?.id, flowNodes, resolvedComponentId]);

  return appliedUiSchemaOptions.autocomplete === false ? (
    <MaterialInputControl {...props} input={MuiSelect} />
  ) : (
    <MuiAutocomplete
      {...props}
      data={copyValue}
      enabled={Boolean(props.enabled && isFactorySelected)}
      isValid={isValid}
      onOpen={() => {
        if (!activeNode?.id || resolvedComponentId === null || resolvedComponentId === undefined || !isFactorySelected) return;
        dispatch(
          factoryManagementActions.getCopyConfigurationFromListRequest({
            factory_id: effectiveFactoryId,
            asset_id: activeNode.id,
            component_id: resolvedComponentId,
          })
        );
      }}
      options={
        props.loadingCopyConfigurationFromList
          ? [{ label: 'Loading...', value: 'loading' }]
          : props.copyConfigurationFromList?.length
          ? props.copyConfigurationFromList
          : fallbackOptions
      }
      handleChange={(path, value) => {
        setCopyValue(value);
        if (value) {
          if (resolvedComponentId === null || resolvedComponentId === undefined) return;
          dispatch(
            factoryManagementActions.getCopyConfigurationFromComponentRequest({
              component_id: resolvedComponentId,
              asset_id: value,
            })
          );
        }
      }}
    />
  );
};

const mapStateToProps = (state: any) => ({
  loadingCopyConfigurationFromList: state.factoryManagement.loadingCopyConfigurationFromList,
  copyConfigurationFromList: state.factoryManagement.copyConfigurationFromList,
  //  copyConfigurationFromComponent: state.factoryManagement.copyConfigurationFromComponent,
});

export default connect(mapStateToProps)(MaterialOneOfEnumControl);
